import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, fileUrl } from "@/lib/api";
import { ArrowLeft, ArrowUpRight, Check, X, Phone, MessageCircle, Truck, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import StarRating from "@/components/StarRating";
import { COMPANY } from "@/data/site";
import { waLink } from "@/lib/whatsapp";

function formatINR(n) {
  if (n == null) return null;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-24" data-testid="product-detail-loading">
        <div className="industrial-card h-96 animate-pulse" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="product-detail-error">
        <div className="eyebrow">Not found</div>
        <h1 className="font-display text-3xl uppercase mt-4">Product not found</h1>
        <Link to="/products" className="btn-secondary mt-6 inline-flex">
          <ArrowLeft size={16} /> Back to catalogue
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#0a0a0c]" data-testid="page-product-detail">
      <Seo
        path={`/products/${product.id}`}
        title={product.name}
        description={product.short_description}
        image={product.image_url ? fileUrl(product.image_url) : undefined}
        type="product"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            image: product.image_url ? [fileUrl(product.image_url)] : undefined,
            description: product.short_description,
            brand: { "@type": "Brand", name: "VOLTECH POWER SOLUTIONS" },
            category: product.category,
            aggregateRating: product.rating
              ? {
                  "@type": "AggregateRating",
                  ratingValue: product.rating,
                  reviewCount: product.rating_count || 1,
                }
              : undefined,
            offers: {
              "@type": "Offer",
              price: product.price || undefined,
              priceCurrency: "INR",
              availability: product.in_stock
                ? "https://schema.org/InStock"
                : "https://schema.org/PreOrder",
              url: `https://voltechpowersolutions.in/products/${product.id}`,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://voltechpowersolutions.in/" },
              { "@type": "ListItem", position: 2, name: "Shop", item: "https://voltechpowersolutions.in/products" },
              { "@type": "ListItem", position: 3, name: product.name, item: `https://voltechpowersolutions.in/products/${product.id}` },
            ],
          })}
        </script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-400 hover:text-[#e63946]"
          data-testid="product-back-link"
        >
          <ArrowLeft size={14} /> Back to catalogue
        </Link>
      </div>

      <section className="py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="industrial-card overflow-hidden">
              {product.image_url ? (
                <img
                  src={fileUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-[420px] md:h-[520px] object-cover"
                  data-testid="product-detail-image"
                />
              ) : (
                <div className="h-[420px] md:h-[520px] flex items-center justify-center text-zinc-600">
                  No image
                </div>
              )}
            </div>
            {product.gallery && product.gallery.length > 0 && (
              <div className="grid grid-cols-4 gap-3 mt-4">
                {product.gallery.map((g, i) => (
                  <div key={i} className="industrial-card overflow-hidden aspect-square">
                    <img src={fileUrl(g)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="tag-chip tag-chip-red">{product.category}</span>
              {product.in_stock ? (
                <span className="tag-chip flex items-center gap-1" style={{ borderColor: "#25d36655", color: "#25d366" }}>
                  <Check size={10} /> In stock
                </span>
              ) : (
                <span className="tag-chip flex items-center gap-1">
                  <X size={10} /> Made to order
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-[1.05] mt-5">
              {product.name}
            </h1>
            {product.rating > 0 && (
              <div className="mt-3">
                <StarRating rating={product.rating} count={product.rating_count || 0} testId="product-detail-rating" />
              </div>
            )}
            <p className="text-zinc-300 mt-5 leading-relaxed">{product.short_description}</p>

            <div className="mt-6 industrial-card p-5" data-testid="product-detail-price-block">
              <div className="eyebrow">Price</div>
              {product.price ? (
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  <span className="font-display text-3xl text-white">{formatINR(product.price)}</span>
                  {product.mrp && product.mrp > product.price && (
                    <>
                      <span className="text-sm text-zinc-500 line-through" data-testid="product-detail-mrp">
                        MRP {formatINR(product.mrp)}
                      </span>
                      <span className="text-xs font-display uppercase text-[#e63946]" data-testid="product-detail-save">
                        Save {Math.round(((product.mrp - product.price) / product.mrp) * 100)}%
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <div className="font-display text-xl mt-1">{product.price_text || "On request"}</div>
              )}
              <div className="mt-3 flex items-center gap-3 text-[11px] text-zinc-400 font-mono uppercase tracking-[0.15em]">
                <span className="flex items-center gap-1"><Truck size={11} className="text-[#e63946]" /> Ships 3–5 days</span>
                <span className="flex items-center gap-1"><ShieldCheck size={11} className="text-[#e63946]" /> {product.warranty || "5-year"} warranty</span>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <a
                  href={waLink(
                    `Hello VPS, I want to buy: ${product.name}${
                      product.price ? ` (${formatINR(product.price)})` : ""
                    }. Please confirm availability and share your best price.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex-1 justify-center"
                  data-testid="product-detail-cta-buy"
                >
                  <MessageCircle size={14} /> Buy on WhatsApp
                </a>
                <Link to="/contact" className="btn-secondary flex-1 justify-center" data-testid="product-detail-cta-quote">
                  Get quote <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a href={`tel:${COMPANY.phoneSales}`} className="btn-secondary flex-1 justify-center" data-testid="product-detail-cta-call">
                <Phone size={14} /> Call sales
              </a>
              <a
                href={waLink(`Hello VPS, I am interested in ${product.name}.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex-1 justify-center"
                data-testid="product-detail-cta-whatsapp"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            </div>

            {product.specs && product.specs.length > 0 && (
              <div className="mt-8" data-testid="product-detail-specs">
                <div className="eyebrow">Specifications</div>
                <div className="mt-3 border border-white/10 divide-y divide-white/10">
                  {product.specs.map((s, i) => (
                    <div key={i} className="grid grid-cols-2 px-4 py-3 text-sm">
                      <span className="text-zinc-500 font-mono uppercase text-[11px] tracking-[0.15em]">
                        {s.label}
                      </span>
                      <span className="text-zinc-100">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {product.long_description && (
        <section className="py-12 border-t border-white/10" data-testid="product-detail-description">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="eyebrow">About this product</div>
            <p className="text-zinc-300 mt-5 leading-relaxed whitespace-pre-line">
              {product.long_description}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
